/**
 * Operator Notification Hooks
 *
 * Separate hooks for operator portal notifications.
 * These hit /api/operator/notifications/* endpoints which query by operatorId
 * (not userId like the dashboard notification hooks in src/hooks/useNotifications.ts).
 *
 * Socket.io gap for pure operators:
 * `emitNotification()` in notification.ts only fires when `userId` is set.
 * Operators without a linked dashboard account have `userId = null` on their
 * notifications -- the `notification:new` socket event will never emit for them.
 * The `refetchInterval: 60000` polling fallback is the sole real-time mechanism
 * for pure operators. The useSocketEventCallback handler below is effectively
 * a no-op for these users but costs nothing and helps hybrid operator+dashboard users.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../../lib/hooks/useApi';
import { useSocketEventCallback } from '../../../lib/socket';
import type { Notification } from '../../../hooks/useNotifications';

// Query keys -- namespaced to avoid collisions with dashboard notification keys
export const operatorNotificationKeys = {
  all: ['operator-notifications'] as const,
  list: (params?: { limit?: number }) =>
    [...operatorNotificationKeys.all, 'list', params] as const,
  unreadCount: () => [...operatorNotificationKeys.all, 'unreadCount'] as const,
};

interface OperatorNotificationListResponse {
  data: Notification[];
  pagination: {
    limit: number;
    offset: number;
    total: number;
  };
  unreadCount: number;
}

/**
 * Fetch operator notifications list
 */
export function useOperatorNotifications(params?: { limit?: number }) {
  const api = useApi();
  const queryClient = useQueryClient();

  // Listen for real-time notifications (works for hybrid operator+dashboard users only)
  useSocketEventCallback('notification:new', () => {
    queryClient.invalidateQueries({ queryKey: operatorNotificationKeys.all });
  });

  return useQuery({
    queryKey: operatorNotificationKeys.list(params),
    queryFn: () => {
      const searchParams = new URLSearchParams();
      if (params?.limit) searchParams.set('limit', String(params.limit));
      const query = searchParams.toString();
      return api.get<OperatorNotificationListResponse>(
        `/api/operator/notifications${query ? `?${query}` : ''}`
      );
    },
  });
}

/**
 * Fetch operator unread count only
 *
 * Polls every 60s as fallback for pure operators without socket events.
 */
export function useOperatorUnreadCount() {
  const api = useApi();
  const queryClient = useQueryClient();

  // Listen for real-time notifications (works for hybrid operator+dashboard users only)
  useSocketEventCallback('notification:new', () => {
    queryClient.invalidateQueries({ queryKey: operatorNotificationKeys.unreadCount() });
  });

  return useQuery({
    queryKey: operatorNotificationKeys.unreadCount(),
    queryFn: () => api.get<{ count: number }>('/api/operator/notifications/unread-count'),
    refetchInterval: 60000, // Poll every minute as fallback for pure operators
  });
}

/**
 * Mark all operator notifications as read
 *
 * On success, invalidates operatorNotificationKeys.all which covers BOTH
 * the list (notification cards re-render as read) AND the unread count badge.
 */
export function useOperatorMarkAllAsRead() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      api.put<{ message: string; markedCount: number }>(
        '/api/operator/notifications/read-all'
      ),
    onSuccess: () => {
      // Invalidate ALL operator notification queries (list + unreadCount)
      queryClient.invalidateQueries({ queryKey: operatorNotificationKeys.all });
    },
  });
}
